import React, { useState } from 'react';
import { X, ImageOff, ExternalLink, Eye, Copy, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import type { SocialPostProduct } from '../../services/socialPosting';

// ── Field Resolution ──────────────────────────────────────────

const FIELD_ALIASES: Record<string, string[]> = {
  image: ['image_link', 'image', 'main_image', 'g:image_link', 'thumbnail', 'image_url'],
  title: ['title', 'name', 'product_title', 'g:title', 'item_title'],
  price: ['price', 'sale_price', 'g:price', 'regular_price'],
  brand: ['brand', 'g:brand', 'manufacturer', 'vendor'],
  availability: ['availability', 'g:availability', 'stock_status'],
  id: ['id', 'g:id', 'sku', 'item_id', 'product_id'],
  link: ['link', 'g:link', 'url', 'product_url'],
};

export function resolveProductField(
  rawData: Record<string, unknown>,
  fieldType: keyof typeof FIELD_ALIASES,
): string {
  const aliases = FIELD_ALIASES[fieldType];
  if (!aliases) return '';
  for (const alias of aliases) {
    const val = rawData[alias];
    if (val !== null && val !== undefined && val !== '') {
      return String(val);
    }
  }
  return '';
}

// ── Component ─────────────────────────────────────────────────

interface ProductCardProps {
  product: SocialPostProduct;
  onRemove?: () => void;
}

// ── Value Renderer ────────────────────────────────────────────

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isImageUrl(value: string): boolean {
  return isUrl(value) && /\.(jpe?g|png|gif|webp|svg|avif|bmp)/i.test(value);
}

function renderFieldValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground italic">—</span>;
  }

  const str = String(value);

  if (isImageUrl(str)) {
    return (
      <div className="space-y-1">
        <img
          src={str}
          alt=""
          className="max-h-32 rounded-lg border object-contain bg-muted/30"
          loading="lazy"
        />
        <a href={str} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all">
          {str.length > 80 ? str.slice(0, 80) + '...' : str}
        </a>
      </div>
    );
  }

  if (isUrl(str)) {
    return (
      <a href={str} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
        {str}
      </a>
    );
  }

  // Long text
  if (str.length > 200) {
    return <p className="text-sm whitespace-pre-wrap break-words">{str}</p>;
  }

  return <span className="text-sm break-words">{str}</span>;
}

// ── Copy Button ──────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className="shrink-0 p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      title="Copy value"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// ── Detail Modal ─────────────────────────────────────────────

function ProductDetailModal({
  product,
  open,
  onClose,
}: {
  product: SocialPostProduct;
  open: boolean;
  onClose: () => void;
}) {
  const image = resolveProductField(product.raw_data, 'image');
  const title = resolveProductField(product.raw_data, 'title');
  const productId = resolveProductField(product.raw_data, 'id');

  const entries = Object.entries(product.raw_data).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-6 text-xl">
            {image && (
              <img src={image} alt="" className="h-12 w-12 rounded-xl object-cover border shrink-0" />
            )}
            <span className="truncate">{title || 'Product Details'}</span>
          </DialogTitle>
          <DialogDescription className="text-sm">
            {productId && <span className="font-mono">{productId}</span>}
            {productId && ` — `}{entries.length} field{entries.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-3">
          <div className="divide-y">
            {entries.map(([key, value]) => (
              <div key={key} className="py-4 grid grid-cols-[160px_1fr_auto] gap-4 items-start">
                <span className="text-sm font-medium text-muted-foreground truncate pt-0.5" title={key}>
                  {key}
                </span>
                <div className="min-w-0">
                  {renderFieldValue(value)}
                </div>
                <CopyButton value={String(value ?? '')} />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Card Component ───────────────────────────────────────────

export function ProductCard({ product, onRemove }: ProductCardProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const image = resolveProductField(product.raw_data, 'image');
  const title = resolveProductField(product.raw_data, 'title');
  const price = resolveProductField(product.raw_data, 'price');
  const brand = resolveProductField(product.raw_data, 'brand');
  const availability = resolveProductField(product.raw_data, 'availability');
  const productId = resolveProductField(product.raw_data, 'id');
  const link = resolveProductField(product.raw_data, 'link');

  const isInStock = availability.toLowerCase().includes('in stock') || availability.toLowerCase().includes('in_stock');
  const isOutOfStock = availability.toLowerCase().includes('out') || availability.toLowerCase().includes('out_of_stock');

  return (
    <>
      <div
        className="group relative bg-card border-2 rounded-xl overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all duration-200 cursor-pointer"
        onClick={() => setShowDetail(true)}
      >
        {/* Hover actions */}
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
            className="bg-background/80 backdrop-blur-sm border rounded-lg p-1 hover:bg-primary hover:text-primary-foreground hover:border-primary"
            title="View details"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          {onRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="bg-background/80 backdrop-blur-sm border rounded-lg p-1 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
              title="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Image */}
        <div className="aspect-square bg-muted/30 flex items-center justify-center overflow-hidden">
          {image && !imgFailed ? (
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <ImageOff className="h-10 w-10 text-muted-foreground/40" />
          )}
        </div>

        {/* Details */}
        <div className="p-3 space-y-2">
          <h4 className="text-sm font-medium line-clamp-2 leading-snug min-h-[2.5rem]">
            {title || 'Untitled Product'}
          </h4>

          {brand && (
            <p className="text-xs text-muted-foreground truncate">{brand}</p>
          )}

          <div className="flex items-center justify-between gap-2">
            {price && (
              <span className="text-sm font-semibold tabular-nums">{price}</span>
            )}
            {availability && (
              <Badge
                variant="outline"
                className={`text-[10px] ${
                  isInStock
                    ? 'bg-green-500/15 text-green-400 border-green-500/30'
                    : isOutOfStock
                      ? 'bg-red-500/15 text-red-400 border-red-500/30'
                      : 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
                }`}
              >
                {availability}
              </Badge>
            )}
          </div>

          <div className="flex items-center justify-between">
            {productId && (
              <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[60%]">
                {productId}
              </span>
            )}
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* Field count hint */}
          <p className="text-[10px] text-muted-foreground">
            {Object.keys(product.raw_data).length} fields
          </p>
        </div>
      </div>

      {/* Detail modal */}
      <ProductDetailModal
        product={product}
        open={showDetail}
        onClose={() => setShowDetail(false)}
      />
    </>
  );
}
